import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { FadeUp, Overline } from "@/components/Reveal";

const BUSINESS_TYPES = ["Distributor", "Reseller", "System Integrator", "Data Centre", "Cloud Operator", "Enterprise", "Government / Institution", "Retailer", "Other"];
const INTERESTS = ["GPU & AI Compute", "Servers & Workstations", "CPUs / Processors", "Memory", "SSD / NVMe / Storage", "Networking", "Computer Components", "Consumer Technology", "Mobile Devices & Accessories", "Gaming / Console Products", "Other Technology"];

const inputCls =
  "w-full bg-white border border-midnight/15 px-4 py-3 text-sm text-midnight placeholder:text-midnight/35 outline-none focus:border-gold transition-colors";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-midnight/50 mb-2";

const Field = ({ label, name, required, value, onChange, placeholder, type = "text" }) => (
  <div>
    <label className={labelCls} htmlFor={name}>{label}{required ? " *" : ""}</label>
    <input id={name} type={type} required={required} value={value} onChange={onChange} placeholder={placeholder} data-testid={`reg-${name}`} className={inputCls} />
  </div>
);

export default function Register() {
  const [form, setForm] = useState({
    company_name: "", website: "", country: "", city: "", reg_number: "", vat_number: "",
    business_type: "", contact_name: "", job_title: "", email: "", secondary_email: "",
    order_value: "", monthly_volume: "", buying_regions: "", delivery_destinations: "",
  });
  const [interests, setInterests] = useState([]);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (i) => setInterests((arr) => (arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i]));

  const submit = async (e) => {
    e.preventDefault();
    if (interests.length === 0) {
      toast.error("Please select at least one commercial interest.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/clients/register", { ...form, interests, marketing_consent: marketing });
      trackEvent("registration_completed", { business_type: form.business_type, interests });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Registration failed. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="register-page">
      <Seo title="Register Your Company — Royal Deals FZ-LLC" description="Create a B2B client profile with Royal Deals FZ-LLC for commercial procurement of advanced compute, enterprise hardware, components and technology products." />
      <Nav />
      <main className="pt-44 pb-24 bg-[#FAFAFA] min-h-screen">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          {done ? (
            <FadeUp>
              <div className="bg-white border border-gold/40 shadow-medium p-10 lg:p-14 text-center" data-testid="register-confirmation">
                <CheckCircle2 size={36} className="text-gold mx-auto mb-6" strokeWidth={1.5} />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Registration Received</p>
                <h1 className="font-heading text-3xl md:text-4xl font-medium text-midnight">Your company profile is with our commercial team.</h1>
                <p className="text-midnight/60 mt-6 max-w-xl mx-auto leading-relaxed">
                  We will review your registration and respond from info@royaldealsfzllc.com. You can submit a hardware requirement at any time.
                </p>
                <Link to="/request" data-testid="register-to-rfq" className="inline-block mt-8 bg-midnight text-white text-sm font-medium px-8 py-4 hover:bg-midnight-light transition-colors">
                  Submit a Requirement
                </Link>
              </div>
            </FadeUp>
          ) : (
            <FadeUp>
              <Overline>Client Registration</Overline>
              <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-midnight">Register your company.</h1>
              <p className="text-midnight/55 mt-6 max-w-xl leading-relaxed">
                Create a B2B client profile with Royal Deals FZ-LLC. Registration is commercial qualification — it is free and creates no obligation.
              </p>

              <form onSubmit={submit} className="mt-12 space-y-10 bg-white hairline shadow-soft p-8 lg:p-12" data-testid="register-form">
                <fieldset>
                  <legend className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-6">01 — Company</legend>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Company Name" name="company_name" required value={form.company_name} onChange={set("company_name")} />
                    <Field label="Company Website" name="website" value={form.website} onChange={set("website")} placeholder="https://" />
                    <Field label="Country" name="country" required value={form.country} onChange={set("country")} />
                    <Field label="City" name="city" value={form.city} onChange={set("city")} />
                    <Field label="Company Registration Number" name="reg_number" value={form.reg_number} onChange={set("reg_number")} />
                    <Field label="VAT / Tax Number" name="vat_number" value={form.vat_number} onChange={set("vat_number")} />
                    <div className="sm:col-span-2">
                      <label className={labelCls} htmlFor="business_type">Business Type *</label>
                      <select id="business_type" required value={form.business_type} onChange={set("business_type")} data-testid="reg-business_type" className={inputCls}>
                        <option value="">Select business type…</option>
                        {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-6">02 — Contact</legend>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Contact Name" name="contact_name" required value={form.contact_name} onChange={set("contact_name")} />
                    <Field label="Job Title" name="job_title" value={form.job_title} onChange={set("job_title")} />
                    <Field label="Business Email" name="email" type="email" required value={form.email} onChange={set("email")} />
                    <Field label="Secondary Email (optional)" name="secondary_email" type="email" value={form.secondary_email} onChange={set("secondary_email")} />
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-6">03 — Commercial Interests *</legend>
                  <div className="flex flex-wrap gap-2.5" data-testid="reg-interests">
                    {INTERESTS.map((i) => (
                      <button
                        type="button"
                        key={i}
                        onClick={() => toggleInterest(i)}
                        data-testid={`reg-interest-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`text-xs font-medium px-4 py-2.5 transition-colors ${interests.includes(i) ? "bg-midnight text-white" : "bg-white text-midnight/65 hairline hover:border-gold"}`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-6">04 — Commercial Profile (optional)</legend>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Typical Order Value" name="order_value" value={form.order_value} onChange={set("order_value")} placeholder="e.g. USD 100k–500k" />
                    <Field label="Approx. Monthly Purchasing Volume" name="monthly_volume" value={form.monthly_volume} onChange={set("monthly_volume")} />
                    <Field label="Main Buying Regions" name="buying_regions" value={form.buying_regions} onChange={set("buying_regions")} placeholder="e.g. GCC, Europe" />
                    <Field label="Main Delivery Destinations" name="delivery_destinations" value={form.delivery_destinations} onChange={set("delivery_destinations")} />
                  </div>
                </fieldset>

                <label className="flex items-start gap-3 cursor-pointer border-t border-midnight/10 pt-6" data-testid="reg-marketing-consent">
                  <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C5A059]" />
                  <span className="text-sm text-midnight/65 leading-relaxed">
                    I would like to receive commercial offers, product availability updates and marketing communications from Royal Deals FZ-LLC. I understand I can unsubscribe at any time.
                  </span>
                </label>

                <button type="submit" disabled={busy} data-testid="register-submit" className="w-full bg-midnight text-white text-sm font-semibold tracking-wide px-8 py-4 hover:bg-midnight-light transition-colors disabled:opacity-50">
                  {busy ? "Registering…" : "Create Client Profile"}
                </button>
                <p className="text-xs text-midnight/40 text-center">Registration and marketing subscription are separate — marketing consent is entirely optional.</p>
              </form>
            </FadeUp>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
